import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

interface PaginationProps {
    currentPage: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    totalItems?: number;
    itemsPerPage?: number;
    showingFrom?: number;
    showingTo?: number;
}

const Pagination: React.FC<PaginationProps> = ({
    currentPage,
    totalPages,
    onPageChange,
    totalItems,
    itemsPerPage,
    showingFrom,
    showingTo,
}) => {
    const getPageNumbers = () => {
        const pages: (number | string)[] = [];
        const maxVisible = 5;

        if (totalPages <= maxVisible) {
            // Show all pages if total pages is less than max visible
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            // Always show first page
            pages.push(1);

            if (currentPage > 3) {
                pages.push('...');
            }

            // Show pages around current page
            const start = Math.max(2, currentPage - 1);
            const end = Math.min(totalPages - 1, currentPage + 1);

            for (let i = start; i <= end; i++) {
                if (i !== 1 && i !== totalPages) {
                    pages.push(i);
                }
            }

            if (currentPage < totalPages - 2) {
                pages.push('...');
            }

            // Always show last page
            if (totalPages > 1) {
                pages.push(totalPages);
            }
        }

        return pages;
    };

    if (totalPages <= 1) {
        return null;
    }

    return (
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-4 border-t border-[#e761a0]">
            {/* Results info - on small screens below controls */}
            {(showingFrom !== undefined && showingTo !== undefined && totalItems !== undefined) && (
                <div className="text-sm font-light text-[#fff] flex gap-[7px] order-2 sm:order-1 sm:flex-1 sm:justify-start">
                    Showing <span className="font-medium text-white">{showingFrom}</span> to{' '}
                    <span className="font-medium text-white">{showingTo}</span> of{' '}
                    <span className="font-medium text-white">{totalItems}</span> results
                </div>
            )}

            {/* Pagination controls - square buttons, centered in middle */}
            <div className="flex items-center justify-end gap-2 order-1 sm:order-2 flex-1">

                {/* Previous page button */}
                <button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="flex items-center justify-center p-2 rounded custom-pagination h-[35px] lg:h-10 border border-[#868686] bg-transparent text-white font-light hover:border-[#E761A0] hover:text-[#E761A0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-[#868686] disabled:hover:text-white"
                    aria-label="Previous page"
                >
                    <ChevronLeft className="w-4 h-4" />
                </button>

                {/* Page numbers */}
                <div className="flex items-center justify-center gap-1">
                    {getPageNumbers().map((page, index) => {
                        if (page === '...') {
                            return (
                                <span
                                    key={`ellipsis-${index}`}
                                    className="flex items-center justify-center px-3 py-2 h-[35px] lg:h-10 text-[#989898] font-light"
                                >
                                    ...
                                </span>
                            );
                        }

                        const pageNum = page as number;
                        const isActive = pageNum === currentPage;

                        return (
                            <button
                                key={pageNum}
                                onClick={() => onPageChange(pageNum)}
                                className={`flex items-center justify-center custom-pagination h-[35px] lg:h-10 px-[5px] lg:px-3 py-2 rounded text-sm font-medium transition-colors ${isActive
                                    ? 'bg-[#E761A0] text-white border border-[#E761A0]'
                                    : 'border border-[#868686] bg-transparent text-white font-light hover:border-[#E761A0] hover:text-[#E761A0]'
                                    }`}
                            >
                                {pageNum}
                            </button>
                        );
                    })}
                </div>

                {/* Next page button */}
                <button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="flex items-center justify-center p-2 rounded custom-pagination h-[35px] lg:h-10 border border-[#868686] bg-transparent text-white font-light hover:border-[#E761A0] hover:text-[#E761A0] transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-[#868686] disabled:hover:text-white"
                    aria-label="Next page"
                >
                    <ChevronRight className="w-4 h-4" />
                </button>

            </div>
        </div>
    );
};

export default Pagination;

